import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle, ArrowLeft, Menu, X, GraduationCap, Users, Calendar, Award } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { useRecaptcha } from '../hooks/useRecaptcha';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { executeRecaptcha } = useRecaptcha();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website: '', // Honeypot field
    form_time: 0
  });
  
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Set form_time when component mounts (anti-spam)
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      form_time: Math.floor(Date.now() / 1000)
    }));
  }, []);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });
    
    try {
      // Execute reCAPTCHA v3
      const captchaToken = await executeRecaptcha('contact');
      
      const response = await axios.post(`${API_URL}/api/contact`, {
        ...formData,
        captcha_token: captchaToken
      });
      
      if (response.data.success) {
        setStatus({ 
          type: 'success', 
          message: response.data.message 
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          website: '',
          form_time: Math.floor(Date.now() / 1000)
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.';
      setStatus({ type: 'error', message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <>
      <SEO 
        title="Contact - DCE Express"
        description="Une question sur nos services d'appels d'offres ? Besoin d'aide pour trouver vos marchés publics ? Notre équipe est à votre écoute."
        path="/contact"
      />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
        {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">DCE</span>
              </div>
              <span className="font-semibold text-slate-800">Mon DCE Express</span>
            </Link>
            <Link 
              to="/"
              className="hidden md:flex items-center text-slate-600 hover:text-indigo-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour à l'accueil
            </Link>
            
            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2 rounded-lg hover:bg-slate-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
          
          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-200">
              <div className="flex flex-col gap-2">
                <Link to="/" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Accueil</Link>
                <Link to="/analyse-dce" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Synthèse IA</Link>
                <Link to="/analyse-gratuite" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Analyse Gratuite</Link>
                <Link to="/blog" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Blog</Link>
                <Link to="/guide-appels-offres" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Guide</Link>
                <div className="border-t border-slate-200 mt-2 pt-4 flex flex-col gap-2 px-4">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full">Connexion</Button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-violet-700 hover:bg-violet-800 text-white">S'inscrire</Button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>
      
      <main className="max-w-6xl mx-auto px-4 py-12 sm:py-16">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Contactez-nous
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Une question sur nos services ? Besoin d'aide pour trouver vos marchés publics ?
            Notre équipe est à votre écoute.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Email</h3>
              <p className="text-slate-600 text-sm mb-3">
                Nous répondons généralement sous 24h
              </p>
              <a 
                href="mailto:progroupement@gmail.com" 
                className="text-indigo-600 hover:text-indigo-700 font-medium"
              >
                progroupement@gmail.com
              </a>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                <Phone className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Téléphone</h3>
              <p className="text-slate-600 text-sm mb-3">
                Du lundi au vendredi, 9h-18h
              </p>
              <span className="text-slate-800 font-medium">
                Sur demande
              </span>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Localisation</h3>
              <p className="text-slate-600 text-sm">
                France métropolitaine<br />
                Service 100% en ligne
              </p>
            </div>
          </div>
          
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <h2 className="text-xl font-semibold text-slate-900 mb-6">
                Envoyez-nous un message
              </h2>
              
              {/* Status Messages */}
              {status.type && (
                <div className={`mb-6 p-4 rounded-xl flex items-start space-x-3 ${
                  status.type === 'success' 
                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' 
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {status.type === 'success' ? (
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  )}
                  <span>{status.message}</span>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field - hidden from users */}
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  style={{ display: 'none' }}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
                
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Jean Dupont"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors"
                      data-testid="contact-name-input"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="jean@entreprise.fr"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-slate-700 mb-2">
                    Sujet
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors bg-white"
                    data-testid="contact-subject-select"
                  >
                    <option value="">Sélectionnez un sujet</option>
                    <option value="Demande de formation">🎓 Demande de formation marchés publics</option>
                    <option value="Question sur les abonnements">Question sur les abonnements</option>
                    <option value="Demande de DCE">Demande de DCE</option>
                    <option value="Problème technique">Problème technique</option>
                    <option value="Partenariat">Partenariat</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Décrivez votre demande en détail..."
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors resize-none"
                    data-testid="contact-message-textarea"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-500">
                    * Champs obligatoires
                  </p>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-200"
                    data-testid="contact-submit-button"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Envoyer le message
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        
        {/* Formation Section */}
        <div className="mt-16 bg-gradient-to-br from-violet-600 to-indigo-700 rounded-3xl p-8 md:p-12 text-white">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm mb-6">
                <GraduationCap className="w-4 h-4" />
                <span>Formation professionnelle</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Vous souhaitez vous former aux marchés publics ?
              </h2>
              <p className="text-violet-100 mb-6">
                Nous proposons des formations sur-mesure pour vous accompagner dans la maîtrise des appels d'offres publics. De la veille à la rédaction de votre offre, développez vos compétences avec nos experts.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Formation individuelle ou équipe</h4>
                    <p className="text-sm text-violet-200">Adaptée à vos besoins</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Flexible</h4>
                    <p className="text-sm text-violet-200">En ligne ou en présentiel</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Certifiée</h4>
                    <p className="text-sm text-violet-200">Éligible OPCO / CPF</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Cas pratiques</h4>
                    <p className="text-sm text-violet-200">Sur vos vrais marchés</p>
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => {
                  setFormData(prev => ({ ...prev, subject: 'Demande de formation' }));
                  document.getElementById('subject')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
                className="inline-flex items-center gap-2 bg-white text-violet-700 px-6 py-3 rounded-xl font-semibold hover:bg-violet-50 transition-colors"
                data-testid="formation-cta-button"
              >
                <GraduationCap className="w-5 h-5" />
                Demander un programme de formation
              </button>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <h3 className="text-xl font-bold mb-4">Nos modules de formation</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium">Initiation aux marchés publics</span>
                    <p className="text-sm text-violet-200">Comprendre le cadre juridique et les procédures</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium">Veille et sourcing des opportunités</span>
                    <p className="text-sm text-violet-200">Identifier les marchés pertinents pour votre activité</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium">Analyse de DCE</span>
                    <p className="text-sm text-violet-200">Lire et décrypter un dossier de consultation</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium">Rédaction du mémoire technique</span>
                    <p className="text-sm text-violet-200">Construire une offre gagnante</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium">Optimisation avec l'IA</span>
                    <p className="text-sm text-violet-200">Utiliser les outils IA pour gagner en efficacité</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* FAQ Section */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
            Questions fréquentes
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                q: "Comment fonctionne l'essai gratuit ?",
                a: "Renseignez votre secteur d'activité et recevez immédiatement 3 marchés publics adaptés à votre profil. Aucune carte bancaire requise."
              },
              {
                q: "Quels sont les délais de réponse ?",
                a: "Nous répondons généralement sous 24h ouvrées. Pour les demandes urgentes, précisez-le dans votre message."
              },
              {
                q: "Comment recevoir mon DCE rapidement ?",
                a: "Avec notre offre 'DCE sous 24h', recevez votre dossier complet par email dans les 24 heures."
              },
              {
                q: "Puis-je modifier mes préférences d'alertes ?",
                a: "Oui, connectez-vous à votre compte et accédez aux paramètres pour gérer vos préférences d'alertes email."
              },
              {
                q: "Proposez-vous des formations sur les marchés publics ?",
                a: "Oui ! Nous proposons des formations individuelles ou en équipe, en présentiel ou à distance. Nos programmes sont éligibles aux financements OPCO et CPF. Contactez-nous pour un programme personnalisé."
              },
              {
                q: "La formation est-elle adaptée aux débutants ?",
                a: "Absolument. Nous avons des modules d'initiation pour les entreprises qui découvrent les marchés publics, ainsi que des formations avancées pour celles qui souhaitent optimiser leur taux de réussite."
              }
            ].map((faq, i) => (
              <div key={`item-${i}`} className="bg-white rounded-xl p-6 border border-slate-100">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-slate-600 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
      
      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-slate-400">
            © 2024 Mon DCE Express - Tous droits réservés
          </p>
        </div>
      </footer>
    </div>
    </>
  );
}
